/**
 * Pure, isolated helper functions for critical driver lifecycle state machines:
 * - Mutex-locked Ride Acceptance (double-tap proof)
 * - Cold Relaunch Active Ride Resolution
 * - Network Interruption Optimistic Rollback
 */

/**
 * Executes ride acceptance with an atomic in-flight mutex lock.
 * Prevents rapid double-taps before React state re-renders.
 */
export function executeRideAccept({
  incoming,
  driver,
  isAcceptingRef,
  acceptRideFn,
  onNavigate,
  onTaken,
  onError,
}) {
  if (!incoming || !incoming.id || !driver) {
    return { status: 'INVALID_INPUT' };
  }

  // Mutex lock check: if a request is already in-flight, immediately drop subsequent taps
  if (isAcceptingRef && isAcceptingRef.current === true) {
    return { status: 'IGNORED_DUPLICATE_TAP' };
  }

  if (isAcceptingRef) isAcceptingRef.current = true;
  const rideId = incoming.id;

  return (async () => {
    try {
      await acceptRideFn(rideId);
      // The callable has now atomically assigned this driver. Navigating only
      // after that confirmation prevents a losing Anna from seeing a trip
      // screen for a ride that another Anna has already claimed.
      if (typeof onNavigate === 'function') {
        onNavigate(`/trip/${rideId}`, {
          state: {
            optimisticRide: {
              ...incoming,
              driverId: driver.id || driver.uid,
              driverName: driver.name,
              driverPhone: driver.phone,
              vehicleNumber: driver.vehicleNumber,
              status: 'driver_assigned',
            },
          },
        });
      }
      return { status: 'SUCCESS', rideId };
    } catch (e) {
      const isTaken = e?.message === 'taken' || String(e?.message || '').includes('already taken');
      if (isTaken && typeof onTaken === 'function') {
        onTaken(rideId);
      } else if (typeof onError === 'function') {
        onError(e);
      }
      return { status: isTaken ? 'ALREADY_TAKEN' : 'ERROR', error: e };
    } finally {
      if (isAcceptingRef) isAcceptingRef.current = false;
    }
  })();
}

/**
 * Resolves the destination route upon cold restart or app un-backgrounding.
 */
export function resolveActiveDriverRedirect({ user, activeRide, currentPath = '/' }) {
  if (!user || !user.uid) return '/login';
  if (
    activeRide &&
    activeRide.id &&
    ['driver_assigned', 'arrived', 'started'].includes(activeRide.status)
  ) {
    const targetRoute = `/trip/${activeRide.id}`;
    if (currentPath !== targetRoute) return targetRoute;
  }
  return null;
}

/**
 * Handles state rollback on network interruption.
 */
export function handleStatusUpdateRollback({ prevState, error, customErrorMessage }) {
  return {
    nextState: prevState,
    errorMessage: customErrorMessage || 'Network connection lost. Please try again.',
    error,
  };
}
